import type { OrganizationDraftYearsResponse } from '@on3/api';
import {
  type ISortFilterItem,
  SortFilter,
} from '@public/components/Db/Filters/SortFilter';
import { ArrowRight } from '@public/components/Svg/ArrowRight';
import { PrimaryButton } from '@public/components/UI/Buttons/PrimaryButton';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { type MouseEventHandler, useCallback, useState } from 'react';

import styles from './DraftHistoryListHeader.module.scss';

const headersList: ISortFilterItem[] = [
  { key: 1, name: 'year', displayName: 'Draft Class', flipDirection: true },
  { key: 2, name: 'fiveStars', displayName: '5-Stars', flipDirection: true },
  { key: 3, name: 'fourStars', displayName: '4-Stars', flipDirection: true },
  { key: 4, name: 'threeStars', displayName: '3-Stars', flipDirection: true },
  { key: 5, name: 'total', displayName: 'Total', flipDirection: true },
  {
    key: 6,
    name: 'blueChipPercent',
    displayName: 'Blue Chip',
    flipDirection: true,
  },
  {
    key: 7,
    name: 'talentRatio',
    displayName: 'Talent Ratio',
    flipDirection: true,
  },
];

interface IDraftHistoryListHeader {
  sortList: (
    value: keyof OrganizationDraftYearsResponse,
    ascending: boolean,
  ) => void;
  selected: keyof OrganizationDraftYearsResponse;
  setSelected: (value: keyof OrganizationDraftYearsResponse) => void;
}
export const DraftHistoryListHeader = ({
  sortList,
  selected,
  setSelected,
}: IDraftHistoryListHeader) => {
  const [ascending, setAscending] = useState(true);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const { dataset } = e.currentTarget;
      const sorttype: keyof OrganizationDraftYearsResponse =
        (dataset.sorttype || 'year') as keyof OrganizationDraftYearsResponse;

      let orderBy = ascending;

      if (sorttype !== selected) {
        orderBy = true;
        setSelected(sorttype);
        setAscending(orderBy);
      } else {
        orderBy = !orderBy;
        setAscending((prev) => !prev);
      }

      sortList(sorttype, orderBy);
    },
    [ascending, selected, setSelected, sortList],
  );

  return (
    <header className={styles.listWrapper}>
      <div className={styles.listContainer}>
        <PrimaryButton
          className={clsx(styles.btnSort, styles.listItem, styles.year, {
            [styles.active]: selected === 'year',
          })}
          data-sortType="year"
          onClick={handleClick}
          variant="text"
        >
          <Text className={styles.title} component="h4" variant="subtitle2">
            draft class
          </Text>
          {selected === 'year' && (
            <ArrowRight
              className={clsx(styles.arrow, { [styles.flip]: !ascending })}
            />
          )}
        </PrimaryButton>
        <div className={styles.starListContainer}>
          <PrimaryButton
            className={clsx(styles.listItem, styles.btnSort, {
              [styles.active]: selected === 'fiveStars',
            })}
            data-sortType="fiveStars"
            onClick={handleClick}
            variant="text"
          >
            <Text className={styles.title} component="h4" variant="subtitle2">
              <span>
                <span className={styles.addDash}>5</span>
                stars
              </span>
            </Text>
            {selected === 'fiveStars' && (
              <ArrowRight
                className={clsx(styles.arrow, { [styles.flip]: !ascending })}
              />
            )}
          </PrimaryButton>

          <PrimaryButton
            className={clsx(styles.listItem, styles.btnSort, {
              [styles.active]: selected === 'fourStars',
            })}
            data-sortType="fourStars"
            onClick={handleClick}
            variant="text"
          >
            <Text className={styles.title} component="h4" variant="subtitle2">
              <span>
                <span className={styles.addDash}>4</span>
                stars
              </span>
            </Text>
            {selected === 'fourStars' && (
              <ArrowRight
                className={clsx(styles.arrow, { [styles.flip]: !ascending })}
              />
            )}
          </PrimaryButton>

          <PrimaryButton
            className={clsx(styles.listItem, styles.btnSort, {
              [styles.active]: selected === 'threeStars',
            })}
            data-sortType="threeStars"
            onClick={handleClick}
            variant="text"
          >
            <Text className={styles.title} component="h4" variant="subtitle2">
              <span>
                <span className={styles.addDash}>3</span>
                stars
              </span>
            </Text>
            {selected === 'threeStars' && (
              <ArrowRight
                className={clsx(styles.arrow, { [styles.flip]: !ascending })}
              />
            )}
          </PrimaryButton>

          <PrimaryButton
            className={clsx(styles.listItem, styles.btnSort, {
              [styles.active]: selected === 'total',
            })}
            data-sortType="total"
            onClick={handleClick}
            variant="text"
          >
            <Text className={styles.title} component="h4" variant="subtitle2">
              total
            </Text>
            {selected === 'total' && (
              <ArrowRight
                className={clsx(styles.arrow, { [styles.flip]: !ascending })}
              />
            )}
          </PrimaryButton>
        </div>
        <ul className={styles.percentListContainer}>
          <PrimaryButton
            className={clsx(styles.listItem, styles.btnSort, {
              [styles.active]: selected === 'blueChipPercent',
            })}
            data-sortType="blueChipPercent"
            onClick={handleClick}
            variant="text"
          >
            <Text className={styles.title} component="h4" variant="subtitle2">
              <span className={styles.draftedTitle}>draft blue chip share</span>{' '}
            </Text>
            {selected === 'blueChipPercent' && (
              <ArrowRight
                className={clsx(styles.arrow, { [styles.flip]: !ascending })}
              />
            )}
          </PrimaryButton>

          <PrimaryButton
            className={clsx(styles.listItem, styles.btnSort, {
              [styles.active]: selected === 'talentRatio',
            })}
            data-sortType="talentRatio"
            onClick={handleClick}
            variant="text"
          >
            <Text className={styles.title} component="h4" variant="subtitle2">
              <span className={styles.draftedTitle}>draft talent ratio</span>{' '}
              <span aria-label="Percentage">%</span>
            </Text>
            {selected === 'talentRatio' && (
              <ArrowRight
                className={clsx(styles.arrow, { [styles.flip]: !ascending })}
              />
            )}
          </PrimaryButton>
        </ul>
      </div>
      <SortFilter
        ascending={ascending}
        className={styles.mobileSort}
        handleSort={handleClick}
        key={`${selected}-${ascending}`}
        selected={selected}
        sortList={headersList}
      />
    </header>
  );
};
